import idx from 'idx';
import { graphql, compose } from 'react-apollo';
import * as React from 'react';

import GET_ORG_MEMBER_QUERY from './queries/OrganizationMemberProfile.gql';
import { OrganizationMemberProfile_organizations } from './queries/__generated__/OrganizationMemberProfile';
import Notice from 'components/Notice';
import UsersProfileContainer, {
  Props as UsersProfileContainerProps,
} from './UsersProfileContainer';
import OrganizationMemberActionsMenuButton from 'user/components/OrganizationMemberActionsMenuButton';
import EventTracking from 'lib/EventTracking';

type Props = UsersProfileContainerProps & { organization: OrganizationMemberProfile_organizations };

type State = { showPrivateNote: boolean };

class UsersProfileInOrgContainer extends React.Component<Props, State> {
  state = {
    showPrivateNote: false,
  };

  setPrivateNoteVisibility = (visible: boolean) => {
    this.setState({
      showPrivateNote: visible,
    });
  };

  trackEvent = (key: string, properties: any = {}) => {
    EventTracking.trackKey(`school_people_list.${key}`, {
      organization_uuid: this.props.organization.uuid,
      ...properties,
    });
  };

  renderUserActionsMenuButton = () => {
    const { organization, member, onClose, onUserChanged } = this.props;
    return (
      <OrganizationMemberActionsMenuButton
        organization={organization}
        member={member}
        isPrivateNoteVisible={!!member.note}
        onRevealPrivateNote={() => this.setPrivateNoteVisibility(true)}
        onUserRemoved={() => {
          this.trackEvent('user_profile.org_membership.removed');
          onClose();
          onUserChanged();
        }}
        onUserUpdate={async (eventName: string = 'user_profile.role.updated') => {
          this.trackEvent(eventName);
          await this.props.refetch();
        }}
        onGetTranscript={requested => {
          const action = requested ? 'requested' : 'sent';
          this.trackEvent(`user_profile.transcript.${action}`);
        }}
      />
    );
  };

  renderErrorBanner = () => {
    const { loading, loaded } = this.props;

    if (!loading && !loaded) {
      return (
        <Notice color="paper">
          This person is not a member of this school so you don’t have permission to view them.
        </Notice>
      );
    }

    return null;
  };

  render() {
    return (
      <UsersProfileContainer
        {...this.props}
        entityName={this.props.organization?.name}
        showPrivateNote={this.state.showPrivateNote}
        userActionsMenuButton={this.renderUserActionsMenuButton()}
        onHidePrivateNote={() => this.setPrivateNoteVisibility(false)}
        onShowPrivateNote={() => this.setPrivateNoteVisibility(true)}
        errorBanner={this.renderErrorBanner()}
        trackEvent={this.trackEvent}
      />
    );
  }
}

const withOrganizationMember = graphql(GET_ORG_MEMBER_QUERY, {
  options: (props: Props) => {
    const { member, entityUuid } = props;

    return {
      variables: {
        orgUuid: entityUuid,
        userId: member.id,
      },
      fetchPolicy: 'network-only',
    };
  },
  props: ({ data, ownProps }: any) => {
    const loadedMember = idx(data, _ => _.organizations[0].members.members[0]);
    const member = (() => {
      if (!data.loading && loadedMember) {
        return {
          groupsJoined: [],
          groupsOwned: [],
          ...loadedMember.user,
          ...loadedMember,
        };
      }
      return ownProps.member;
    })();
    return {
      organization: idx(data['organizations'], _ => _[0]),
      member,
      loading: data.loading,
      loaded: !!loadedMember,
      error: data.error,
      refetch: data.refetch,
    };
  },
  skip: ({ entityUuid }) => !entityUuid,
});

export default compose(withOrganizationMember)(UsersProfileInOrgContainer);
